import { useState, useEffect, useRef } from 'react'
import { Manager, Popper, Reference } from 'react-popper'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
import Paper from 'components/report/Paper'
import CircleSpinner from 'components/misc/CircleSpinner'
import { Fade } from 'components/misc/Fade'
import api from 'lib/api'
import * as analytics from 'lib/analytics'
import styles from './styles/PaperLink.css'
import tooltipStyles from 'components/misc/styles/Tooltip.css'
import Paragraph from '../typography/Paragraph'
import { highlightText } from '../../lib/highlight-text'

const loadPaper = async ({ doi, setPaper, setTally, setLoading }) => {
  try {
    const [{ data: paper }, { data: tally }] = await Promise.all([
      api.get(`/papers/${doi}`),
      api.get(`/tallies/${doi}`)
    ])
    setPaper(paper)
    setTally(tally)
  } catch (e) {
    return
  } finally {
    setLoading(false)
  }
}

const PaperMetadata = ({ doi }) => {
  const [loading, setLoading] = useState(true)
  const [paper, setPaper] = useState({})
  const [tally, setTally] = useState({})

  useEffect(() => {
    loadPaper({ doi, setPaper, setTally, setLoading })
  }, [doi])

  const handleViewSciteReport = () => {
    analytics.event({
      category: 'Search Citations',
      action: 'view-tooltip-linked-report',
      label: doi
    })
  }

  if (loading) {
    return <CircleSpinner />
  }

  return (
    <Paper
      paper={paper}
      tally={tally}
      onViewSciteReport={handleViewSciteReport}
    />
  )
}

const PaperMetadataLoader = ({
  text,
  doi,
  delay = 400,
  hideDelay = 200,
  metadataLoaderClassName = null,
  textMatch = '',
  popperPlacement = 'top',
  children
}) => {
  const popupTimeout = useRef(null)
  const [show, setShow] = useState(false)
  const [overTooltip, setOverTooltip] = useState(false)
  const [initialRender, setInitialRender] = useState(true)

  const handleMouseEnter = () => {
    popupTimeout.current = setTimeout(() => {
      setShow(true)
    }, delay)
  }

  const handleMouseLeave = () => {
    if (popupTimeout.current) {
      window.clearTimeout(popupTimeout.current)
    }
    setTimeout(() => {
      setShow(false)
    }, hideDelay)
  }

  useEffect(() => {
    setInitialRender(false)
  }, [])

  if (initialRender) {
    return children
  }

  return (
    <Manager>
      <Reference>
        {({ ref }) => (
          <span
            ref={ref}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {children}
          </span>
        )}
      </Reference>
      {createPortal(
        <Fade
          className={classNames(
            styles.popperWrapper, metadataLoaderClassName
          )} show={show || overTooltip}
        >
          <Popper placement={popperPlacement}>
            {({ ref, style, placement, arrowProps }) => (
              <div
                className={classNames(
                  tooltipStyles.popperBase,
                  styles.metadataTooltip
                )}
                ref={ref}
                style={style}
                data-placement={placement}
                onMouseEnter={_ => setOverTooltip(true)}
                onMouseLeave={_ => {
                  setTimeout(() => {
                    setOverTooltip(false)
                  }, hideDelay)
                }}
              >
                {text && (
                  <Paragraph className={styles.snippetPreview} light small>
                    "...
                    <span dangerouslySetInnerHTML={{ __html: highlightText(text, textMatch) }} />
                    ..."
                  </Paragraph>
                )}
                <PaperMetadata doi={doi} />
                <div
                  className={classNames(
                    tooltipStyles.arrow,
                    tooltipStyles.arrowWhite
                  )}
                  ref={arrowProps.ref}
                  style={arrowProps.style}
                />
              </div>
            )}
          </Popper>
        </Fade>,
        document.querySelector('#scite-tooltips')
      )}
    </Manager>
  )
}

const PaperLink = ({
  children,
  doi,
  isTarget,
  linkTarget,
  metadataDelay,
  metadataHideDelay,
  text,
  textMatch,
  popperPlacement,
  metadataLoaderClassName,
  link = true,
  ...rest
}) => {
  const className = isTarget ? styles.targetCite : styles.paperLink

  if (!doi) {
    return (
      <a className={className} {...rest}>
        {children}
      </a>
    )
  }

  if (isTarget && !linkTarget) {
    return (
      <span className={className} {...rest}>
        {children}
      </span>
    )
  }

  return (
    <PaperMetadataLoader
      text={text}
      textMatch={textMatch}
      doi={doi}
      delay={metadataDelay}
      hideDelay={metadataHideDelay}
      metadataLoaderClassName={metadataLoaderClassName}
      popperPlacement={popperPlacement}
    >
      <a
        className={className}
        href={link ? `/reports/${doi}` : null}
        target='_blank'
        rel='noopener noreferrer'
        {...rest}
      >
        {children}
      </a>
    </PaperMetadataLoader>
  )
}

export default PaperLink
